import { transliterate } from '../constants/transliterate';
import { ReactNode } from 'react';

const stringUtils = {
  generateRandomUniqString: (length: number): string => {
    let result = '';
    const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    const charactersLength = characters.length;
    let counter = 0;
    while (counter < length) {
      result += characters.charAt(Math.floor(Math.random() * charactersLength));
      counter += 1;
    }
    return result;
  },
  stringIsEmpty: (str: string): boolean => {
    return str.length === 0;
  },
  changeTransliterate: (text: PlainText, toLatin: boolean): PlainText => {
    if (!text) {
      return '' as PlainText;
    }

    const transliterateMap: Record<string, string> = toLatin
      ? transliterate.cyrillicMap
      : transliterate.latinMap;

    const combinedMap: Record<string, string> = toLatin
      ? transliterate.combinedCyrillicMap
      : transliterate.combinedLatinMap;

    const result: string[] = [];
    let i = 0;
    while (i < text.length) {
      const currentChar = text[i];
      const nextChar = text[i + 1];
      const inOneChar = text[i + 2];

      if (currentChar === '%') {
        const endIdx = text.indexOf('%', i + 1);
        if (endIdx !== -1) {
          result.push(text.substring(i, endIdx + 1));
          i = endIdx + 1;
          continue;
        }
      }

      if (Object.prototype.hasOwnProperty.call(combinedMap, currentChar + nextChar + inOneChar)) {
        const transliterated = combinedMap[currentChar + nextChar + inOneChar];
        const isUpperCase = currentChar === currentChar.toUpperCase();
        result.push(isUpperCase ? transliterated.toUpperCase() : transliterated);
        i += 3;
      } else if (Object.prototype.hasOwnProperty.call(combinedMap, currentChar + nextChar)) {
        const transliterated = combinedMap[currentChar + nextChar];
        const isUpperCase = currentChar === currentChar.toUpperCase();
        result.push(isUpperCase ? transliterated.toUpperCase() : transliterated);
        i += 2;
      } else {
        const transliterated = transliterateMap[currentChar.toLowerCase()];
        if (transliterated !== undefined) {
          const isUpperCase = currentChar === currentChar.toUpperCase();
          result.push(isUpperCase ? transliterated.toUpperCase() : transliterated);
        } else {
          result.push(currentChar);
        }
        i++;
      }
    }

    return result.join('') as PlainText;
  },
  extractString: (value: string, regex: RegExp) => {
    const matches = value.match(regex);

    if (matches) {
      return matches;
    } else {
      return [];
    }
  },
  extractLinkAndCleanText: (text: PlainText) => {
    const linkRegex = /<a\s+[^>]*href=["']([^"']*)["'][^>]*>([^<]*)<\/a>/i;
    const match = text.match(linkRegex);

    if (!match) {
      return {
        cleanedText: text,
        extractedLink: null,
        insertionIndex: -1,
      };
    }

    const [fullMatch, href, linkText] = match;
    const insertionIndex = text.indexOf(fullMatch);

    const cleanedText = text.replace(fullMatch, '');

    return {
      cleanedText,
      extractedLink: {
        href,
        text: linkText,
        fullTag: fullMatch,
      },
      insertionIndex,
    };
  },
  insertLink(text: PlainText, link: ReactNode, index: number): ReactNode[] {
    if (index < 0 || index > text.length) {
      return [text];
    }

    const before = text.slice(0, index);
    const after = text.slice(index);

    return [before, link, after].filter(Boolean);
  },
  getFormattedHTMLText(htmlText?: HTMLString): HTMLString {
    return htmlText?.replace(/(\r\n|\r|\n)/g, '\n') as HTMLString;
  },
  cleanStringByRegex(inputString: string, allowedRegex: RegExp): string {
    return inputString.replace(allowedRegex, '');
  },
  getShortFirstName(fullName?: string): string | undefined {
    if (!fullName) return undefined;
    const parts = fullName
      .trim()
      .replace(/\s{2,}/g, ' ')
      .split(' ');
    const name = parts.length > 1 ? parts[1] : parts[0];
    return name.substring(0, 20);
  },
};

export { stringUtils };
